// AdminPanelFirebase.jsx
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, auth, storage } from "../firebase";
import * as XLSX from "xlsx";
import CategoryForm from "./CategoryForm";
import ItemForm from "./ItemForm";
import ConfirmModal from "./ConfirmModal";
import ActionButton from "./ActionButton";
import ImageUploader from "./ImageUploader";
import "../styles/AdminPanelFirebase.css";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { FiDownload, FiTrash2, FiFolderPlus, FiFolderMinus, FiChevronDown, FiChevronRight, FiChevronUp, FiArrowUp, FiArrowDown, FiEdit2, FiX, FiCheck, FiLoader, FiSearch } from "react-icons/fi";

const menuRef = collection(db, "menu");

export default function AdminPanelFirebase() {
  const [menu, setMenu] = useState([]);
  const [category, setCategory] = useState("");
  const [categoryIconUrl, setCategoryIconUrl] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [editingCategoryIconUrl, setEditingCategoryIconUrl] = useState("");
  const [selectedCatId, setSelectedCatId] = useState("");
  const [itemNameHy, setItemNameHy] = useState("");
  const [itemNameEn, setItemNameEn] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [itemInfo, setItemInfo] = useState("");
  const [confirmDelete, setConfirmDelete] = useState({
    visible: false,
    type: null,
    payload: null,
  });
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCats, setExpandedCats] = useState({});
  const [dragOverTarget, setDragOverTarget] = useState(null); // Հետևում է քաշվող էլեմենտի թիրախին

  // Check if user is logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        window.location.href = "/login";
      }
    });
    return () => unsubscribe();
  }, []);

  // Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Load menu from Firestore
  const loadMenu = async () => {
    const snapshot = await getDocs(menuRef);
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    data.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    setMenu(data);
  };

  useEffect(() => {
    loadMenu();
  }, []);

  // Category handlers
  const addCategory = async () => {
    if (!category) return;
    const highestOrder = menu.reduce((max, sec) => Math.max(max, sec.order ?? 0), 0);
    await addDoc(menuRef, {
      category,
      iconUrl: categoryIconUrl,
      items: [],
      order: highestOrder + 1,
    });
    setCategory("");
    setCategoryIconUrl("");
    loadMenu();
  };

  const startEditingCategory = (cat) => {
    setEditingCategory(cat);
    setEditingCategoryName(cat.category);
    setEditingCategoryIconUrl(cat.iconUrl || "");
  };

  const editCategory = async () => {
    if (!editingCategory || !editingCategoryName) return;
    const ref = doc(db, "menu", editingCategory.id);
    await updateDoc(ref, {
      category: editingCategoryName,
      iconUrl: editingCategoryIconUrl,
    });
    setEditingCategory(null);
    setEditingCategoryName("");
    setEditingCategoryIconUrl("");
    loadMenu();
  };

  const cancelCategoryEdit = () => {
    setEditingCategory(null);
    setEditingCategoryName("");
    setEditingCategoryIconUrl("");
  };

  const askDeleteCategory = (id) => {
    setConfirmDelete({ visible: true, type: "category", payload: id });
  };

  const moveCategoryUp = async (index) => {
    if (index === 0) return;
    const current = menu[index];
    const prev = menu[index - 1];
    await Promise.all([
      updateDoc(doc(db, "menu", current.id), { order: prev.order }),
      updateDoc(doc(db, "menu", prev.id), { order: current.order }),
    ]);
    loadMenu();
  };

  const moveCategoryDown = async (index) => {
    if (index === menu.length - 1) return;
    const current = menu[index];
    const next = menu[index + 1];
    await Promise.all([
      updateDoc(doc(db, "menu", current.id), { order: next.order }),
      updateDoc(doc(db, "menu", next.id), { order: current.order }),
    ]);
    setTimeout(loadMenu, 300);
  };

  // Item handlers
  const addItem = async () => {
    if ((!itemNameHy && !itemNameEn) || !itemPrice || !selectedCatId) return;
    const ref = doc(db, "menu", selectedCatId);
    await updateDoc(ref, {
      items: arrayUnion({
        nameHy: itemNameHy,
        nameEn: itemNameEn,
        price: itemPrice,
        imageUrl,
        info: itemInfo,
      }),
    });
    setItemNameHy("");
    setItemNameEn("");
    setItemPrice("");
    setImageUrl("");
    setItemInfo("");
    loadMenu();
  };

  const startEditingItem = (catId, item, idx) => {
    setSelectedCatId(catId);
    setItemNameHy(item.nameHy || "");
    setItemNameEn(item.nameEn || "");
    setItemPrice(item.price);
    setImageUrl(item.imageUrl || "");
    setItemInfo(item.info || "");
    setEditingItem({ original: item, index: idx });
  };

  const editItem = async () => {
    if (!editingItem || !selectedCatId) return;
    
    const oldCat = menu.find((c) => c.items?.includes(editingItem.original));
    const oldCatId = oldCat ? oldCat.id : null;

    const updatedItemObj = { nameHy: itemNameHy, nameEn: itemNameEn, price: itemPrice, imageUrl, info: itemInfo };

    if (oldCatId === selectedCatId) {
      const ref = doc(db, "menu", selectedCatId);
      const updatedItems = oldCat.items.map((item) =>
        item === editingItem.original ? updatedItemObj : item
      );
      await updateDoc(ref, { items: updatedItems });
    } else {
      if (oldCatId) {
        const oldRef = doc(db, "menu", oldCatId);
        await updateDoc(oldRef, { items: arrayRemove(editingItem.original) });
      }
      const newRef = doc(db, "menu", selectedCatId);
      await updateDoc(newRef, { items: arrayUnion(updatedItemObj) });
    }

    setEditingItem(null);
    setItemNameHy("");
    setItemNameEn("");
    setItemPrice("");
    setImageUrl("");
    setItemInfo("");
    loadMenu();
  };

  const cancelItemEdit = () => {
    setEditingItem(null);
    setItemNameHy("");
    setItemNameEn("");
    setItemPrice("");
    setImageUrl("");
    setItemInfo("");
  };

  const moveItemUp = async (catId, index) => {
    if (index === 0) return;
    const category = menu.find((cat) => cat.id === catId);
    if (!category) return;
    const items = [...(category.items || [])];
    [items[index - 1], items[index]] = [items[index], items[index - 1]];
    await updateDoc(doc(db, "menu", catId), { items });
    loadMenu();
  };

  const moveItemDown = async (catId, index) => {
    const category = menu.find((cat) => cat.id === catId);
    if (!category) return;
    if (index === (category.items?.length ?? 0) - 1) return;
    const items = [...(category.items || [])];
    [items[index], items[index + 1]] = [items[index + 1], items[index]];
    await updateDoc(doc(db, "menu", catId), { items });
    loadMenu();
  };

  const askDeleteItem = (catId, item) => {
    setConfirmDelete({ visible: true, type: "item", payload: { catId, item } });
  };

  const askDeleteAll = () => {
    setConfirmDelete({ visible: true, type: "all", payload: null });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.visible) return;
    if (confirmDelete.type === "category") {
      const categoryToDelete = menu.find((c) => c.id === confirmDelete.payload);
      if (categoryToDelete?.iconUrl?.includes("firebasestorage")) {
        try {
          await deleteObject(ref(storage, categoryToDelete.iconUrl));
        } catch (error) {
          console.error("Error deleting category icon from storage:", error);
        }
      }
      await deleteDoc(doc(db, "menu", confirmDelete.payload));
    } else if (confirmDelete.type === "item") {
      const { catId, item } = confirmDelete.payload;
      if (item.imageUrl?.includes("firebasestorage")) {
        try {
          await deleteObject(ref(storage, item.imageUrl));
        } catch (error) {
          console.error("Error deleting item image from storage:", error);
        }
      }
      await updateDoc(doc(db, "menu", catId), { items: arrayRemove(item) });
    } else if (confirmDelete.type === "all") {
      for (const cat of menu) {
        if (cat.iconUrl?.includes("firebasestorage")) {
          try { await deleteObject(ref(storage, cat.iconUrl)); } catch (e) { console.error(e); }
        }
        if (cat.items) {
          for (const item of cat.items) {
            if (item.imageUrl?.includes("firebasestorage")) {
              try { await deleteObject(ref(storage, item.imageUrl)); } catch (e) { console.error(e); }
            }
          }
        }
        await deleteDoc(doc(db, "menu", cat.id));
      }
    }
    setConfirmDelete({ visible: false, type: null, payload: null });
    loadMenu();
  };

  const handleCancelDelete = () => {
    setConfirmDelete({ visible: false, type: null, payload: null });
  };

  const toggleCat = (id) => {
    setExpandedCats((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const allExpanded = {};
    menu.forEach((cat) => {
      allExpanded[cat.id] = true;
    });
    setExpandedCats(allExpanded);
  };

  const collapseAll = () => {
    setExpandedCats({});
  };

  // Excel Import Handler
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(wb.Sheets[wsname]);

        const menuMap = {};
        data.forEach((row) => {
          const cat = row.Category || row.category || row["Բաժին"];
          if (!cat) return; // Բաց է թողնում առանց բաժին տողերը
          if (!menuMap[cat]) menuMap[cat] = [];
          menuMap[cat].push({
            nameHy: row.NameHy || row.nameHy || row["Անուն (Հայ)"] || String(row.Name || ""),
            nameEn: row.NameEn || row.nameEn || row["Անուն (Անգլ)"] || "",
            price: String(row.Price || row.price || row["Գին"] || ""),
            info: row.Info || row.info || row["Նկարագրություն"] || "",
            imageUrl: row.ImageUrl || row.imageUrl || row["Նկարի հղում"] || "",
          });
        });

        let currentHighestOrder = menu.reduce((max, sec) => Math.max(max, sec.order ?? 0), 0);
        for (const [catName, itemsToAdd] of Object.entries(menuMap)) {
          const existingCat = menu.find((c) => c.category === catName);
          if (existingCat) {
            const docRef = doc(db, "menu", existingCat.id);
            await updateDoc(docRef, { items: [...(existingCat.items || []), ...itemsToAdd] });
          } else {
            currentHighestOrder++;
            await addDoc(menuRef, { category: catName, iconUrl: "", items: itemsToAdd, order: currentHighestOrder });
          }
        }
        alert("Մենյուն հաջողությամբ ներմուծվեց Excel-ից!");
        loadMenu();
      } catch (error) {
        console.error("Import error:", error);
        alert("Սխալ ներմուծման ժամանակ: Ստուգեք ֆայլի ֆորմատը:");
      } finally {
        setImporting(false);
        e.target.value = null; // Մաքրում ենք input-ը
      }
    };
    reader.readAsBinaryString(file);
  };

  // --- DRAG AND DROP ՀԱՄԱՐ ՖՈՒՆԿՑԻԱՆԵՐ ---
  const handleCategoryDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ type: "category", index }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleItemDragStart = (e, catId, itemIndex) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ type: "item", catId, itemIndex }));
    e.dataTransfer.effectAllowed = "move";
    e.stopPropagation(); // Որպեսզի ապրանք քաշելիս, կատեգորիան էլ հետը չքաշվի
  };

  const handleDragEnd = () => {
    setDragOverTarget(null); // Մաքրում է վիզուալ էֆեկտը քաշելն ավարտելիս
  };

  const handleItemDrop = async (e, toCatId, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null); // Մաքրել վիզուալը
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (data.type === "item") {
        const { catId: fromCatId, itemIndex: fromIndex } = data;
        if (fromIndex === undefined || !fromCatId) return;

        const newMenu = [...menu];
        const fromCatIndex = newMenu.findIndex((c) => c.id === fromCatId);
        const toCatIndex = newMenu.findIndex((c) => c.id === toCatId);

        if (fromCatIndex === -1 || toCatIndex === -1) return;

        if (fromCatId === toCatId) {
          // Տեղափոխություն ՆՈՒՅՆ կատեգորիայի մեջ
          if (fromIndex === toIndex) return;
          const newItems = [...newMenu[fromCatIndex].items];
          const [movedItem] = newItems.splice(fromIndex, 1);
          
          if (toIndex === -1) newItems.push(movedItem); // Գցել ենք ամենավերջում
          else newItems.splice(toIndex, 0, movedItem);
          
          newMenu[fromCatIndex].items = newItems;
          setMenu(newMenu); // Էկրանին միանգամից թարմացնում ենք
          await updateDoc(doc(db, "menu", fromCatId), { items: newItems });
        } else {
          // Տեղափոխություն ԱՅԼ կատեգորիայի մեջ
          const newFromItems = [...newMenu[fromCatIndex].items];
          const [movedItem] = newFromItems.splice(fromIndex, 1);
          const newToItems = [...(newMenu[toCatIndex].items || [])];
          
          if (toIndex === -1) newToItems.push(movedItem);
          else newToItems.splice(toIndex, 0, movedItem);

          newMenu[fromCatIndex].items = newFromItems;
          newMenu[toCatIndex].items = newToItems;
          setMenu(newMenu);

          await Promise.all([
            updateDoc(doc(db, "menu", fromCatId), { items: newFromItems }),
            updateDoc(doc(db, "menu", toCatId), { items: newToItems })
          ]);
        }
      }
    } catch (err) {
      console.error("Drop error", err);
    }
  };

  const handleCategoryDrop = async (e, toIndex, toCatId) => {
    e.preventDefault();
    setDragOverTarget(null); // Մաքրել վիզուալը
    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (data.type === "category") {
        const fromIndex = data.index;
        if (fromIndex === toIndex || fromIndex === undefined) return;
        const newMenu = [...menu];
        const [movedCat] = newMenu.splice(fromIndex, 1);
        newMenu.splice(toIndex, 0, movedCat);
        setMenu(newMenu);
        await Promise.all(newMenu.map((cat, i) => updateDoc(doc(db, "menu", cat.id), { order: i + 1 })));
      } else if (data.type === "item") {
        handleItemDrop(e, toCatId, -1); // Ապրանքը գցում ենք կատեգորիայի վրա՝ ավելացնելով վերջից
      }
    } catch (err) {}
  };

  let modalImage = null;
  if (confirmDelete.visible) {
    if (confirmDelete.type === "category") {
      const cat = menu.find((c) => c.id === confirmDelete.payload);
      modalImage = cat?.iconUrl;
    } else if (confirmDelete.type === "item") {
      modalImage = confirmDelete.payload?.item?.imageUrl;
    }
  }

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      <button
        onClick={handleLogout}
        style={{ padding: "8px 16px", background: "#f44336", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
      >
        Logout
      </button>
      <button
        onClick={loadMenu}
        style={{ margin: "10px", padding: "8px 16px", cursor: "pointer" }}
      >
        Refresh Menu
      </button>

      <label style={{ margin: "10px", padding: "8px 16px", cursor: "pointer", background: "#4CAF50", color: "#fff", border: "none", borderRadius: 4, display: "inline-block" }}>
        {importing ? <span style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiLoader /> Բեռնվում է...</span> : <span style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiDownload /> Ներմուծել Excel-ից</span>}
        <input type="file" accept=".xlsx, .xls, .csv" onChange={handleImportExcel} disabled={importing} style={{ display: "none" }} />
      </label>

      <button
        onClick={askDeleteAll}
        style={{ margin: "10px", padding: "8px 16px", cursor: "pointer", background: "#f44336", color: "#fff", border: "none", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: "6px" }}
      >
        <FiTrash2 /> Ջնջել ամբողջ մենյուն
      </button>

    {!editingCategory && (
      <CategoryForm
        category={category}
        categoryIconUrl={categoryIconUrl}
        setCategory={setCategory}
        setCategoryIconUrl={setCategoryIconUrl}
        addCategory={addCategory}
      />
    )}

      <hr />

      {!editingItem && (
        <div>
          <ItemForm
            menu={menu}
            selectedCatId={selectedCatId}
            setSelectedCatId={setSelectedCatId}
            itemNameHy={itemNameHy}
            setItemNameHy={setItemNameHy}
            itemNameEn={itemNameEn}
            setItemNameEn={setItemNameEn}
            itemPrice={itemPrice}
            setItemPrice={setItemPrice}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            itemInfo={itemInfo}
            setItemInfo={setItemInfo}
            addItem={addItem}
            editingItem={editingItem}
            editItem={editItem}
            cancelItemEdit={cancelItemEdit}
          />
        </div>
      )}

      <hr />

      <div style={{ position: "sticky", top: 0, zIndex: 10, background: "white", padding: "15px 0", borderBottom: "1px solid #eee" }}>
        <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
          <button onClick={expandAll} style={{ padding: "8px 16px", cursor: "pointer", borderRadius: "4px", border: "1px solid #ccc", background: "#fff", display: "flex", alignItems: "center", gap: "6px" }}>
            <FiFolderPlus /> Բացել բոլորը
          </button>
          <button onClick={collapseAll} style={{ padding: "8px 16px", cursor: "pointer", borderRadius: "4px", border: "1px solid #ccc", background: "#fff", display: "flex", alignItems: "center", gap: "6px" }}>
            <FiFolderMinus /> Փակել բոլորը
          </button>
        </div>

        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#888", display: "flex" }}>
            <FiSearch size={18} />
          </div>
          <input
            type="text"
            placeholder="Որոնել բաժին կամ ապրանք..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "100%", padding: "10px 40px 10px 38px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", boxSizing: "border-box" }}
          />
          {searchTerm && (
            <div
              onClick={() => setSearchTerm("")}
              style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#999", cursor: "pointer", display: "flex" }}
              title="Մաքրել որոնումը"
            >
              <FiX size={20} />
            </div>
          )}
        </div>
      </div>

      {menu
        .map((sec) => {
          if (!searchTerm) return sec;
          const term = searchTerm.toLowerCase();
          const catMatches = sec.category?.toLowerCase().includes(term);
          const matchedItems = sec.items?.filter(
            (item) =>
              item.nameHy?.toLowerCase().includes(term) ||
              item.nameEn?.toLowerCase().includes(term) ||
              item.info?.toLowerCase().includes(term)
          );
          if (!catMatches && (!matchedItems || matchedItems.length === 0)) return null;
          return { ...sec, items: catMatches ? sec.items : matchedItems };
        })
        .filter(Boolean)
        .map((sec) => {
          const originalIndex = menu.findIndex((c) => c.id === sec.id);
          const isExpanded = searchTerm ? true : expandedCats[sec.id];

          return (
            <div 
              key={sec.id} 
              draggable={!searchTerm} // Արգելափակել որոնման ռեժիմում
              onDragStart={(e) => handleCategoryDragStart(e, originalIndex)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragOverTarget !== `cat-${sec.id}`) setDragOverTarget(`cat-${sec.id}`);
              }}
              onDragLeave={() => setDragOverTarget(null)}
              onDrop={(e) => handleCategoryDrop(e, originalIndex, sec.id)}
              style={{ 
                border: "1px solid #ddd", 
                marginBottom: "10px", 
                borderRadius: "8px", 
                background: dragOverTarget === `cat-${sec.id}` ? "#e8f5e9" : "#fafafa",
                outline: dragOverTarget === `cat-${sec.id}` ? "2px dashed #4CAF50" : "none",
                outlineOffset: "2px",
                transition: "all 0.2s ease"
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px", cursor: "pointer", background: "#f0f0f0", borderRadius: isExpanded ? "8px 8px 0 0" : "8px" }}
                onClick={() => toggleCat(sec.id)}
              >
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "24px" }}>{isExpanded ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}</span>
                {sec.iconUrl && <img src={sec.iconUrl} alt="" style={{ width: 24, height: 24, objectFit: "contain" }} />}
                <h3 style={{ margin: 0, flexGrow: 1 }}>{sec.category} <span style={{ fontSize: "14px", color: "#666" }}>({sec.items?.length || 0})</span></h3>
                <div onClick={(e) => e.stopPropagation()}>
                  <ActionButton onAction={() => moveCategoryUp(originalIndex)} disabled={!!searchTerm || originalIndex === 0}><FiArrowUp /></ActionButton>
                  <ActionButton onAction={() => moveCategoryDown(originalIndex)} disabled={!!searchTerm || originalIndex === menu.length - 1}><FiArrowDown /></ActionButton>
                  <ActionButton onAction={() => startEditingCategory(sec)}><FiEdit2 /></ActionButton>
                  <ActionButton onAction={() => askDeleteCategory(sec.id)}><FiX /></ActionButton>
                </div>
              </div>

              {isExpanded && (
                <>
                  <ul 
                  style={{ 
                    listStyle: "none", 
                    padding: "10px", 
                    margin: 0, 
                    minHeight: "40px",
                    background: dragOverTarget === `cat-ul-${sec.id}` ? "#f1f8ff" : "transparent",
                    borderRadius: "6px",
                    transition: "all 0.2s ease"
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (dragOverTarget !== `cat-ul-${sec.id}`) setDragOverTarget(`cat-ul-${sec.id}`);
                  }}
                  onDragLeave={() => setDragOverTarget(null)}
                  onDrop={(e) => handleItemDrop(e, sec.id, -1)}
                >
                  {sec.items?.map((item) => {
                    const origItemIdx = menu.find((c) => c.id === sec.id)?.items.findIndex((i) => i === item);

                    return (
                      <li 
                        key={origItemIdx} 
                        className="admin-item-list-row"
                        draggable={!searchTerm}
                        onDragStart={(e) => handleItemDragStart(e, sec.id, origItemIdx)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (dragOverTarget !== `item-${sec.id}-${origItemIdx}`) setDragOverTarget(`item-${sec.id}-${origItemIdx}`);
                        }}
                        onDragLeave={(e) => {
                          e.stopPropagation();
                          setDragOverTarget(null);
                        }}
                        onDrop={(e) => handleItemDrop(e, sec.id, origItemIdx)}
                        style={{ 
                          cursor: searchTerm ? "default" : "grab",
                          background: dragOverTarget === `item-${sec.id}-${origItemIdx}` ? "#e3f2fd" : "",
                          outline: dragOverTarget === `item-${sec.id}-${origItemIdx}` ? "2px dashed #2196F3" : "none",
                          outlineOffset: "2px",
                          transform: dragOverTarget === `item-${sec.id}-${origItemIdx}` ? "scale(1.02)" : "scale(1)",
                          transition: "all 0.2s ease",
                          zIndex: dragOverTarget === `item-${sec.id}-${origItemIdx}` ? 10 : 1,
                          position: "relative"
                        }}
                      >
                        {item.imageUrl && <img src={item.imageUrl} alt="" className="admin-item-image" />}
                        <span className="admin-item-details">
                          {item.nameEn} / {item.nameHy} - {item.price} ֏
                          {item.info && <div className="admin-item-info">{item.info}</div>}
                        </span>
                        <ActionButton onAction={() => moveItemUp(sec.id, origItemIdx)} disabled={!!searchTerm || origItemIdx === 0}><FiArrowUp /></ActionButton>
                        <ActionButton onAction={() => moveItemDown(sec.id, origItemIdx)} disabled={!!searchTerm || origItemIdx === (menu.find((c) => c.id === sec.id)?.items.length - 1)}><FiArrowDown /></ActionButton>
                        <ActionButton onAction={() => startEditingItem(sec.id, item, origItemIdx)}><FiEdit2 /></ActionButton>
                        <ActionButton onAction={() => askDeleteItem(sec.id, item)}><FiTrash2 /></ActionButton>
                      </li>
                    );
                  })}
                </ul>
                  <div style={{ textAlign: 'center', padding: '8px 0', borderTop: '1px solid #eee', background: '#fafafa', borderRadius: '0 0 7px 7px' }}>
                    <button 
                      onClick={() => toggleCat(sec.id)} 
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px' }}
                      title="Փակել բաժինը"
                    >
                      <FiChevronUp /> Փակել
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}

      {confirmDelete.visible && (
        <ConfirmModal
          message={
            confirmDelete.type === "category" ? "Դուք ցանկանում եք ջնջել այս բաժինը?" : 
            confirmDelete.type === "item" ? "Դուք ցանկանում եք ջնջել այս կետը?" : 
            "ԶԳՈՒՇԱՑՈՒՄ: Դուք վստա՞հ եք, որ ցանկանում եք ջնջել ԱՄԲՈՂՋ ՄԵՆՅՈՒՆ։ Այս գործողությունը անդառնալի է։"
          }
          imageUrl={modalImage}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      {/* Modal for Editing Category */}
      {editingCategory && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "white", padding: "25px", borderRadius: "12px", width: "90%", maxWidth: "500px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
            <h3 style={{ marginTop: 0, color: "#2196F3", marginBottom: "15px" }}>Խմբագրել բաժինը</h3>
            <input
              placeholder="Բաժնի նոր անուն"
              value={editingCategoryName}
              onChange={(e) => setEditingCategoryName(e.target.value)}
              style={{ width: "100%", padding: "10px", marginBottom: "15px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "16px", boxSizing: "border-box" }}
            />
            <ImageUploader
              value={editingCategoryIconUrl}
              onChange={setEditingCategoryIconUrl}
              placeholder="Icon URL"
            />
            <div style={{ marginTop: "20px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={cancelCategoryEdit} style={{ padding: "8px 16px", background: "#f44336", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}><FiX /> Չեղարկել</button>
              <button onClick={editCategory} style={{ padding: "8px 16px", background: "#4CAF50", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}><FiCheck /> Պահպանել</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Editing Item */}
      {editingItem && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "white", padding: "25px", borderRadius: "12px", width: "90%", maxWidth: "550px", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
            <ItemForm
              menu={menu}
              selectedCatId={selectedCatId}
              setSelectedCatId={setSelectedCatId}
              itemNameHy={itemNameHy}
              setItemNameHy={setItemNameHy}
              itemNameEn={itemNameEn}
              setItemNameEn={setItemNameEn}
              itemPrice={itemPrice}
              setItemPrice={setItemPrice}
              imageUrl={imageUrl}
              setImageUrl={setImageUrl}
              itemInfo={itemInfo}
              setItemInfo={setItemInfo}
              addItem={addItem}
              editingItem={editingItem}
              editItem={editItem}
              cancelItemEdit={cancelItemEdit}
            />
          </div>
        </div>
      )}
    </div>
  );
}
